<template>
  <div>

    <div class="h-panel">
      <b-button :to="{name: 'player_inventory'}">
        Cancel
      </b-button>
      <b-button
        v-if="isLoadedNormal"
        @click="save"
        variant="primary"
      >
        Save
      </b-button>
    </div>

    <loading-indicator
      v-if="isLoading"
    />

    <table v-if="inventoryItems !== null" class="table text-nowrap inventory-table">
      <thead class="thead-dark">
        <tr>
          <th scope="col">Product</th>
          <th scope="col" class="text-right">Amount</th>
          <th scope="col" class="text-right">Delta</th>
          <th scope="col" class="text-right">New amount</th>
        </tr>
      </thead>
      <tbody>
        <template v-for="item in inventoryItems">
          <item-editor
            :key="item.id"
            :item="item"
            ref="itemEditor"
          />
        </template>
      </tbody>
    </table>

  </div>
</template>

<script>
import { HTTP } from '../http-common.js';
import notifier from '../notifier.js';

/**
 * @typedef { import("../model/player_inventory.js").InventoryItem     } InventoryItem
 * @typedef { import("../model/player_inventory.js").InventoryItemData } InventoryItemData
 */
import { compareInventoryItems } from '../model/player_inventory.js';

import LoadingIndicator from './LoadingIndicator.vue';
import ItemEditor from './PlayerInventoryEditor/ItemEditor.vue';

const LOAD_STATE_INIT          = 1;
const LOAD_STATE_LOADING       = 2;
const LOAD_STATE_LOADED_NORMAL = 3;
const LOAD_STATE_LOADED_ERROR  = 4;

export default {
  name: 'PlayerInventoryEdit',

  components: {
    LoadingIndicator,
    ItemEditor,
  },

  props: {
    playerId: {
      type: Number,
      required: true,
    }
  },

  data() {
    return {
      loadState: LOAD_STATE_INIT,

      /** @type {?InventoryItem[]} */
      inventoryItems: null,
    };
  },

  computed: {
    /** @returns {boolean} */
    isLoading() {
      return this.loadState === LOAD_STATE_LOADING;
    },

    /** @returns {boolean} */
    isLoadedNormal() {
      return this.loadState === LOAD_STATE_LOADED_NORMAL;
    },

    itemDatas() {
      /** @type {InventoryItemData[]} */
      const datas = [];

      const itemEditors = this.$refs.itemEditor;
      if(itemEditors === undefined) {
        return null;
      }

      for(const itemEditor of itemEditors) {
        const data = itemEditor.getChangedItemData();
        if(data !== null) {
          datas.push(data);
        }
      }

      return datas;
    },

    /** @returns {boolean} */
    hasChanges() {
      const datas = this.itemDatas;
      return (datas !== null) && (datas.length > 0);
    },

    /** @returns {boolean} */
    noChanges() {
      return !this.hasChanges;
    },
  },

  created() {
    this.loadInventory();
  },

  methods: {
    loadInventory() {
      notifier.deleteSenderNotifications(this);

      this.loadState = LOAD_STATE_LOADING;

      const url = '/player/' + this.playerId + '/inventory';
      HTTP.get(url)
      .then(response => {
        /** @type {InventoryItem[]} */
        const inventoryItems = response.data.data;
        inventoryItems.sort(compareInventoryItems);

        this.inventoryItems = inventoryItems;

        this.loadState = LOAD_STATE_LOADED_NORMAL;
      })
      .catch(err => {
        this.loadState = LOAD_STATE_LOADED_ERROR;
        notifier.showError(err);
      });
    },

    save() {
      const datas = this.itemDatas;

      const hasChanges = (datas !== null) && (datas.length > 0);
      if(!hasChanges) {
        notifier.showWarning('No changes.');
        return;
      }

      notifier.deleteSenderNotifications(this);

      const url = '/player/' + this.playerId + '/inventory';
      HTTP.post(url, datas)
      .then(() => {
        notifier.showSuccessAfterTransition('Changes saved.');
        this.$router.push({name: 'player_inventory'});
      })
      .catch(err => {
        notifier.showError(err, {sender: this});
      })
    },
  },
};
</script>
